import { useMemo } from 'react';
import {
  AiOutlineCheck,
  AiOutlineClose,
  AiOutlineDelete,
  AiOutlineEdit,
  AiOutlineEye,
} from 'react-icons/ai';
import { TbRestore } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import { Card } from '../../../../../../components/card/card';
import { IconButton } from '../../../../../../components/icon-button/icon-button';
import { HasAccess } from '../../../../../../features/has-access/has-access';
import { Table } from '../../../../../../features/table/table';
import { TableFilterInput } from '../../../../../../features/table/table-filter/table-filter-input/table-filter-input';
import { TableFilterMultiSelect } from '../../../../../../features/table/table-filter/table-filter-multi-select/table-filter-multi-select';
import { TableFilterSingleSelect } from '../../../../../../features/table/table-filter/table-filter-single-select/table-filter-single-select';
import { withAllBranches } from '../../../../../../helpers/mappers';
import { buildParams, concatPath } from '../../../../../../helpers/path';
import { useLang } from '../../../../../../hooks/use-lang';
import { useModalParams } from '../../../../../../hooks/use-modal-params';
import { useQueryParams } from '../../../../../../hooks/use-query-params';
import { DevicesPath, ManagerPath, RootPath } from '../../../../../../routes/manager/manager-enums';
import { useBranchesStore } from '../../../../../../store/use-branch-store';
import { useQrStore } from '../../../../../../store/use-qr-store';
import s from './devices-qr-table.module.css';

export const DevicesQrTable = () => {
  const { t, currentLang, getLocale } = useLang();
  const { dynamicList, isLoadingFetch } = useQrStore();
  const { place } = useBranchesStore();
  const branches = useMemo(() => withAllBranches(place, currentLang), [currentLang, place]);
  const { addQueryParams } = useQueryParams();
  const viewDynamicQrModal = useModalParams('viewDynamicQr');

  return (
    <Card>
      <Table
        id="dynamic-list"
        isLoading={isLoadingFetch}
        columnSettings
        frontPagination
        withArchive
        className={s.table}
        onEmpty={{
          title: t('Список QR кодов пуст'),
          text: t('Создайте первый QR код с помощью кнопки выше'),
        }}
        cols={{
          name: {
            label: t('Название'),
            filter: (
              <TableFilterInput
                name="name"
                suggestOptions={dynamicList?.map((item) => getLocale(item.name))}
              />
            ),
          },
          description: {
            label: t('Описание'),
            maxWidth: 200,
            filter: (
              <TableFilterInput
                name="description"
                suggestOptions={dynamicList?.map((item) => getLocale(item.description))}
              />
            ),
          },
          branch: {
            label: t('Филиал'),
            filter: (
              <TableFilterMultiSelect
                name="branch"
                options={place?.map((item) => ({
                  id: item.id.toString(),
                  value: getLocale(item.name),
                }))}
              />
            ),
          },
          createdAt: {
            label: t('Привязан'),
            filter: (
              <TableFilterSingleSelect
                name="is_attached"
                options={[
                  { id: 'true', value: t('Да') },
                  { id: 'false', value: t('Нет') },
                ]}
              />
            ),
          },
          actions: { label: t('Действия') },
        }}
        rows={dynamicList?.map((item) => ({
          name: getLocale(item.name),
          description: getLocale(item.description),
          branch: branches.find((branch) => branch.id === String(item.place_id))?.value,
          createdAt: item.is_attached ? (
            <AiOutlineCheck className={s.attachedIcon} />
          ) : (
            <AiOutlineClose className={s.inAttachedIcon} />
          ),
          actions: item.is_deleted ? (
            <HasAccess service="device" permissions={['write']}>
              <IconButton
                tooltip={t('Восстановить')}
                variant="secondary"
                size="sm"
                onClick={() =>
                  addQueryParams({
                    restoreId: String(item.id),
                    restoreName: getLocale(item.name),
                  })
                }
              >
                <TbRestore />
              </IconButton>
            </HasAccess>
          ) : (
            <div className={s.actions}>
              <IconButton
                tooltip={t('Просмотр')}
                variant="secondary"
                size="sm"
                onClick={() =>
                  viewDynamicQrModal.handleOpenModal({
                    qrId: item.id,
                    qrName: getLocale(item.name),
                    code: item.code || '',
                    isAttached: item.is_attached ? 1 : 0,
                  })
                }
              >
                <AiOutlineEye />
              </IconButton>

              <HasAccess service="device" permissions={['write']}>
                <Link
                  to={
                    concatPath([RootPath.Manager, ManagerPath.Devices, DevicesPath.Edit]) +
                    buildParams({ id: item.id })
                  }
                >
                  <IconButton tooltip={t('Редактировать')} size="sm" variant="secondary">
                    <AiOutlineEdit />
                  </IconButton>
                </Link>
                <IconButton
                  tooltip={t('Удалить')}
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    addQueryParams({
                      deleteId: item.id,
                      deleteName: getLocale(item.name),
                    })
                  }
                >
                  <AiOutlineDelete />
                </IconButton>
              </HasAccess>
            </div>
          ),
        }))}
      />
    </Card>
  );
};
